import { useRating } from '@risksmart-app/components/src/hooks/useRating';
import { useMemo } from 'react';

import { useRiskScore } from '@/hooks/useRiskScore';
import { useRiskRatingResolver } from '@/ratings/useRiskRatingResolver';

import { useControlTypeLabel } from '../../../../assessments/forms/useControlTypeLabel';
import { UNRATED } from '../../../../controls/lookupData';
import type {
  ComplianceRiskAssessmentResultFlatFields,
  ComplianceRiskAssessmentResultRegisterFields,
} from './types';

export const useComplianceRatingLabelledFields = (
  riskId: string,
  records: ComplianceRiskAssessmentResultFlatFields[] | undefined,
  configIds: string[]
) => {
  const statusRating = useRating('assessment_status', 'oversight');
  const getControlTypeLabel = useControlTypeLabel();
  const riskScores = useRiskScore(riskId);
  const { resolveRiskRating, resolveImpact, resolveLikelihood } =
    useRiskRatingResolver('oversight', {
      configType: 'oversight',
      configIds,
    });

  return useMemo<ComplianceRiskAssessmentResultRegisterFields[]>(() => {
    return (
      records?.map((d) => {
        const oversightAssessment = d.parents.find(
          (p) => p.oversightAssessment
        )?.oversightAssessment;

        return {
          ...d,
          StatusLabelled:
            oversightAssessment !== undefined
              ? (statusRating.getLabel(oversightAssessment?.Status) ?? '-')
              : '-',
          NextTestDate: oversightAssessment?.NextTestDate ?? '-',
          ActualCompletionDate:
            oversightAssessment?.ActualCompletionDate ?? '-',
          ParentTitle: oversightAssessment?.Title ?? '-',
          ControlTypeLabelled:
            getControlTypeLabel(d.ControlType) || UNRATED.label,
          CompletionDate: oversightAssessment?.ActualCompletionDate ?? '-',
          RatingLabelled: !riskScores.showScore
            ? (resolveRiskRating({
                likelihood: d.Likelihood,
                impact: d.Impact,
                controlType: d.ControlType,
                rating: d.Rating,
                configId: d.ConfigId,
              })?.label ?? UNRATED.label)
            : '-',
          ImpactLabelled:
            resolveImpact(d.Impact, d.ConfigId)?.label ?? UNRATED.label,
          LikelihoodLabelled:
            resolveLikelihood(d.Likelihood, d.ConfigId)?.label ?? UNRATED.label,
        };
      }) || []
    );
  }, [
    records,
    statusRating,
    getControlTypeLabel,
    riskScores,
    resolveRiskRating,
    resolveImpact,
    resolveLikelihood,
  ]);
};
